import {
    getFirestore,
    collection,
    query,
    where,
    onSnapshot,
    doc,
    getDoc,
    updateDoc,
    Timestamp,
} from 'firebase/firestore';
import { app } from '@/Firebase.js';

/**
 * Named Firestore DB used by lixsa-admin for all admin data.
 * Uses the main Firebase app (lixsa-prod) which carries the auth token.
 */
const db = getFirestore(app, 'lixsa-dashboard');

/**
 * Maps a Firestore document snapshot to a camelCase recipient object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap - The Firestore document snapshot.
 * @returns {Object} The recipient object with camelCase fields.
 */
function mapRecipientDoc(docSnap) {
    const d = docSnap.data();
    return {
        id: docSnap.id,
        firebaseUid: d.firebase_uid,
        announcementId: d.announcement_id,
        dismissed: d.dismissed,
        dismissedAt: d.dismissed_at,
        ctaClicked: d.cta_clicked,
        ctaClickedAt: d.cta_clicked_at,
        createdAt: d.created_at,
    };
}

/**
 * Maps a Firestore announcement document to a camelCase object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap - The Firestore document snapshot.
 * @returns {Object|null} The announcement object with camelCase fields, or null if not found.
 */
function mapAnnouncementDoc(docSnap) {
    if (!docSnap.exists()) return null;
    const d = docSnap.data();
    return {
        id: docSnap.id,
        title: d.title,
        steps: (d.steps || []).map((s) => ({
            order: s.order,
            htmlContent: s.html_content,
            ctaText: s.cta_text,
            ctaUrl: s.cta_url,
            ctaExternal: s.cta_external,
        })),
        showDismissButton: d.show_dismiss_button ?? true,
        status: d.status,
        createdAt: d.created_at,
        updatedAt: d.updated_at,
    };
}

/**
 * Subscribes to pending (non-dismissed) announcements for a given Firebase user.
 * Uses onSnapshot for real-time updates.
 * @param {string} firebaseUid - The Firebase Auth UID (localId).
 * @param {Function} callback - Called with an array of recipient objects on each snapshot.
 * @returns {Function} The Firestore unsubscribe function.
 */
export function subscribeToPendingAnnouncements(firebaseUid, callback) {
    const recipientsRef = collection(db, 'feature_announcement_recipients');
    const q = query(recipientsRef, where('firebase_uid', '==', firebaseUid), where('dismissed', '==', false));

    return onSnapshot(
        q,
        (snapshot) => {
            const recipients = snapshot.docs.map(mapRecipientDoc);
            callback(recipients);
        },
        (error) => {
            console.error('[AnnouncementFirestore] Subscription error:', error);
        },
    );
}

/**
 * Fetches the full announcement content (HTML steps, title, etc.) by ID.
 * @param {string} announcementId - The Firestore document ID of the announcement.
 * @returns {Promise<Object|null>} The announcement data with camelCase fields, or null if not found.
 */
export async function getAnnouncementContent(announcementId) {
    const docRef = doc(db, 'feature_announcements', announcementId);
    const docSnap = await getDoc(docRef);
    return mapAnnouncementDoc(docSnap);
}

/**
 * Marks a recipient's announcement as dismissed.
 * @param {string} recipientId - The Firestore document ID of the recipient record.
 * @returns {Promise<void>}
 */
export async function dismissAnnouncement(recipientId) {
    const docRef = doc(db, 'feature_announcement_recipients', recipientId);
    await updateDoc(docRef, {
        dismissed: true,
        dismissed_at: Timestamp.now(),
    });
}

/**
 * Records that the user clicked the CTA link on an announcement.
 * @param {string} recipientId - The Firestore document ID of the recipient record.
 * @returns {Promise<void>}
 */
export async function markCtaClicked(recipientId) {
    const docRef = doc(db, 'feature_announcement_recipients', recipientId);
    await updateDoc(docRef, {
        cta_clicked: true,
        cta_clicked_at: Timestamp.now(),
    });
}
